class CollectionFilterDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = null;
    this.panel = null;
    this.form = null;
    this.activeElement = null;
    this._scrollLocked = false;
    this._lockScrollY = 0;
    this._isRendering = false;
    this._appliedFormHtml = '';
    this._boundClick = this.onClick.bind(this);
    this._boundDocumentClick = this.onDocumentClick.bind(this);
    this._boundKeyUp = this.onKeyUp.bind(this);
    this._boundPopState = this.onPopState.bind(this);
    this._boundSubmit = this.onSubmit.bind(this);
    this._boundChange = this.onChange.bind(this);
  }

  connectedCallback() {
    const existing = document.querySelector('collection-filter-drawer');
    if (existing && existing !== this) {
      this.remove();
      return;
    }

    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
      return;
    }

    if (this.dataset.filterDrawerReady === 'true') return;
    this.dataset.filterDrawerReady = 'true';

    this.overlay = this.querySelector('[data-collection-filter-overlay]');
    this.panel = this.querySelector('[data-collection-filter-panel]');
    this.form = this.querySelector('[data-collection-filter-form]');

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    this.addEventListener('click', this._boundClick);
    if (this.form) {
      this.form.addEventListener('submit', this._boundSubmit);
      this.form.addEventListener('change', this._boundChange);
    }
    this.addEventListener('keyup', this._boundKeyUp);
    document.addEventListener('click', this._boundDocumentClick);
    window.addEventListener('popstate', this._boundPopState);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._boundDocumentClick);
    window.removeEventListener('popstate', this._boundPopState);
  }

  onPopState() {
    if (!this.sectionId || !document.querySelector('#collection-results')) return;
    this.applyUrl(window.location.href, { push: false, closeDrawer: true });
  }

  isMobileViewport() {
    return window.matchMedia('(max-width: 63.99em)').matches;
  }

  onDocumentClick(event) {
    const trigger = event.target.closest('[data-collection-filter-open]');
    if (trigger && !this.contains(trigger)) {
      event.preventDefault();
      this.open({ triggeredBy: trigger, instant: this.isMobileViewport() });
      return;
    }

    const sortRemove = event.target.closest('[data-collection-filter-remove="sort"]');
    if (sortRemove) {
      event.preventDefault();
      const url = new URL(window.location.href);
      url.searchParams.delete('sort_by');
      this.applyUrl(url.toString(), { closeDrawer: false });
      return;
    }

    const pill = event.target.closest('[data-collection-filter-pill]');
    if (pill && !this.contains(pill)) {
      event.preventDefault();
      const href = pill.getAttribute('href');
      if (href) this.applyUrl(href, { closeDrawer: false });
    }
  }

  onClick(event) {
    if (event.target.closest('[data-collection-filter-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.target.closest('[data-collection-filter-reset]')) {
      event.preventDefault();
      this.applyUrl(this.collectionUrl, { closeDrawer: true });
      return;
    }

    const sortChipRemove = event.target.closest('.collection-filter-drawer__option-remove');
    if (sortChipRemove) {
      const option = sortChipRemove.closest('.collection-filter-drawer__option');
      const sortInput = option && option.querySelector('input[name="sort_by"]');
      if (sortInput && sortInput.checked) {
        event.preventDefault();
        sortInput.checked = false;
        this.syncSelectedOptions(option.closest('.collection-filter-drawer__group'));
      }
    }
  }

  onChange(event) {
    const option = event.target.closest('.collection-filter-drawer__option');
    if (!option) return;
    this.syncSelectedOptions(option.closest('.collection-filter-drawer__group'));
  }

  syncSelectedOptions(group) {
    if (!group) return;
    const selected = group.querySelector('[data-collection-filter-selected]');
    const list = group.querySelector('[data-collection-filter-options]');
    if (!selected || !list) return;

    group.querySelectorAll('.collection-filter-drawer__option').forEach((opt) => {
      const input = opt.querySelector('input');
      const dest = input && input.checked ? selected : list;
      if (dest && opt.parentElement !== dest) dest.appendChild(opt);
    });
  }

  onSubmit(event) {
    event.preventDefault();
    const url = this.buildUrlFromForm();
    this.applyUrl(url, { closeDrawer: true });
  }

  onKeyUp(event) {
    if (event.code === 'Escape' || event.key === 'Escape') {
      this.close();
    }
  }

  get sectionId() {
    return this.dataset.sectionId || '';
  }

  get collectionUrl() {
    return this.dataset.collectionUrl || window.location.pathname;
  }

  buildUrlFromForm() {
    const url = new URL(this.collectionUrl, window.location.origin);
    if (!this.form) return url.toString();

    const formData = new FormData(this.form);
    formData.forEach((value, key) => {
      if (value === '' || value == null) return;
      url.searchParams.append(key, value);
    });
    return url.toString();
  }

  open(options = {}) {
    if (this.classList.contains('is-open')) return;

    const triggeredBy = options.triggeredBy;
    const instant = Boolean(options.instant);
    if (triggeredBy) this.activeElement = triggeredBy;
    this.snapshotAppliedFilters();

    this.closeOtherDrawers();

    if (instant) {
      this.classList.add('is-instant');
    } else {
      this.classList.remove('is-instant');
    }

    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.documentElement.setAttribute('data-active-modal', 'collection-filter');
    this.lockPageScroll();
    this.syncTriggerExpanded(true);

    const focusTarget = this.querySelector('[data-collection-filter-close]') || this.panel;
    if (typeof trapFocus === 'function' && this.panel) {
      try {
        trapFocus(this.panel, focusTarget);
      } catch (error) {
        console.error('CollectionFilterDrawer: trapFocus failed', error);
      }
    } else if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus();
    }
  }

  snapshotAppliedFilters() {
    if (!this.form) return;
    this._appliedFormHtml = this.form.innerHTML;
  }

  restoreAppliedFilters() {
    if (!this.form || !this._appliedFormHtml) return;
    if (this.form.innerHTML === this._appliedFormHtml) return;

    try {
      this.form.innerHTML = this._appliedFormHtml;
    } catch (error) {
      console.error('CollectionFilterDrawer: failed to clear unapplied filters', error);
    }
  }

  close(options = {}) {
    if (!this.classList.contains('is-open')) return;

    if (options.restoreDraft !== false) {
      this.restoreAppliedFilters();
    }

    const returnFocusTo = this.activeElement;
    this.classList.remove('is-open');
    this.classList.remove('is-instant');
    this.setAttribute('aria-hidden', 'true');
    document.documentElement.removeAttribute('data-active-modal');
    this.unlockPageScroll();
    this.syncTriggerExpanded(false);

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(returnFocusTo);
    } else if (returnFocusTo && typeof returnFocusTo.focus === 'function') {
      returnFocusTo.focus();
    }
  }

  syncTriggerExpanded(expanded) {
    document.querySelectorAll('[data-collection-filter-open]').forEach((el) => {
      el.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  closeOtherDrawers() {
    const searchDrawer = document.querySelector('search-drawer');
    if (searchDrawer && searchDrawer.classList.contains('is-open') && typeof searchDrawer.close === 'function') {
      try {
        searchDrawer.close();
      } catch (error) {
        console.error('CollectionFilterDrawer: failed to close search drawer', error);
      }
    }

    const cart = document.querySelector('cart-drawer');
    if (cart && cart.classList.contains('active') && typeof cart.close === 'function') {
      try {
        cart.close();
      } catch (error) {
        console.error('CollectionFilterDrawer: failed to close cart drawer', error);
      }
    }
  }

  lockPageScroll() {
    if (this._scrollLocked) return;
    this._scrollLocked = true;
    this._lockScrollY = window.scrollY || window.pageYOffset || 0;

    document.documentElement.classList.add('collection-filter-drawer-scroll-lock');
    document.body.classList.add('overflow-hidden');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._lockScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  unlockPageScroll() {
    if (!this._scrollLocked) return;
    this._scrollLocked = false;

    document.documentElement.classList.remove('collection-filter-drawer-scroll-lock');
    document.body.classList.remove('overflow-hidden');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('left');
    document.body.style.removeProperty('right');
    document.body.style.removeProperty('width');

    const y = this._lockScrollY || 0;
    this._lockScrollY = 0;
    window.scrollTo(0, y);

    document.documentElement.scrollLeft = 0;
    document.body.scrollLeft = 0;
    if (document.scrollingElement) document.scrollingElement.scrollLeft = 0;
  }

  async applyUrl(urlString, options = {}) {
    const push = options.push !== false;
    const closeDrawer = Boolean(options.closeDrawer);

    if (this._isRendering) return;
    this._isRendering = true;
    this.classList.add('is-loading');

    const results = document.querySelector('#collection-results');
    if (results) results.classList.add('is-loading');

    try {
      const sectionUrl = this.sectionUrl(urlString);
      const response = await fetch(sectionUrl, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Filter request failed (${response.status})`);

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      this.replaceResults(parsed);
      this.replacePills(parsed);
      this.replaceForm(parsed);

      if (push) {
        const next = new URL(urlString, window.location.origin);
        next.searchParams.delete('section_id');
        window.history.pushState({ collectionFilter: true }, '', next.pathname + next.search);
      }

      if (closeDrawer) this.close({ restoreDraft: false });

      document.dispatchEvent(new CustomEvent('collection:filters-updated'));

      const toolbar = document.querySelector('[data-collection-filter-toolbar]');
      if (toolbar && closeDrawer) {
        toolbar.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      console.error('CollectionFilterDrawer: failed to update collection', error);
      window.location.href = urlString;
    } finally {
      this._isRendering = false;
      this.classList.remove('is-loading');
      if (results) results.classList.remove('is-loading');
    }
  }

  sectionUrl(urlString) {
    const url = new URL(urlString, window.location.origin);
    if (this.sectionId) url.searchParams.set('section_id', this.sectionId);
    return url.toString();
  }

  replaceResults(parsed) {
    const current = document.querySelector('#collection-results');
    const next = parsed.querySelector('#collection-results');
    if (!current || !next) return;
    current.replaceWith(next);
  }

  replacePills(parsed) {
    const currentList = document.querySelectorAll('[data-collection-filter-pills]');
    const next = parsed.querySelector('[data-collection-filter-pills]');
    if (!next || !currentList.length) return;
    currentList.forEach((el) => {
      el.replaceWith(next.cloneNode(true));
    });
  }

  replaceForm(parsed) {
    const nextForm = parsed.querySelector('[data-collection-filter-form]');
    if (!nextForm || !this.form) return;

    const nextDrawer = parsed.querySelector('collection-filter-drawer');
    if (nextDrawer && nextDrawer.dataset.collectionUrl) {
      this.dataset.collectionUrl = nextDrawer.dataset.collectionUrl;
    }
    if (nextDrawer && nextDrawer.dataset.sectionId) {
      this.dataset.sectionId = nextDrawer.dataset.sectionId;
    }

    this.form.innerHTML = nextForm.innerHTML;
  }
}

if (!customElements.get('collection-filter-drawer')) {
  customElements.define('collection-filter-drawer', CollectionFilterDrawer);
}

const updateFilterPillScrollHints = () => {
  document.querySelectorAll('[data-collection-filter-pills]').forEach((pills) => {
    const overflow = pills.scrollWidth > pills.clientWidth + 2;
    const atEnd = pills.scrollLeft + pills.clientWidth >= pills.scrollWidth - 2;
    pills.classList.toggle('is-scrollable', overflow);
    pills.classList.toggle('is-scrolled-end', atEnd);
  });
};

document.addEventListener(
  'scroll',
  (event) => {
    const target = event.target;
    if (target instanceof Element && target.matches('[data-collection-filter-pills]')) {
      updateFilterPillScrollHints();
    }
  },
  true
);
window.addEventListener('resize', updateFilterPillScrollHints);
document.addEventListener('collection:filters-updated', updateFilterPillScrollHints);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', updateFilterPillScrollHints);
} else {
  updateFilterPillScrollHints();
}
